import { ChevronRight, ExternalLink, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { PluginManifest, PluginWebManifest } from '@reflex/plugin-api';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { Badge } from '@reflex/ui/components/ui/badge';
import { MetaListItem } from '@reflex/ui/components/shared/MetaListItem';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { cn } from '@reflex/ui/lib/utils';
import { LucideIconByName } from '@/pages/admin/plugin-detail/LucideIconByName';
import { withOrgSlug } from '@/lib/routes';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';

interface ContributionItem {
  /** Stable react key. */
  key: string;
  /** Primary label rendered in normal weight text. */
  label: string;
  /** Optional small caption rendered next to / under the label. */
  hint?: string;
  /** Optional icon name resolved via `LucideIconByName`. */
  icon?: string;
  /** When set, the row links to this plugin-declared path. */
  href?: string;
  /** When true, {@link href} is not prefixed with the active org slug. */
  rootLevel?: boolean;
  /** When true, the link opens in a new browser tab. */
  openInNewWindow?: boolean;
}

interface CategoryDef {
  key: string;
  label: string;
  caption: string;
  items: ContributionItem[];
}

function collect(manifest: PluginManifest): CategoryDef[] {
  const web:
    | (PluginWebManifest & { agentType?: { name: string; tagline: string; icon: string } })
    | undefined = manifest.web;
  if (!web) return [];

  const navItems = web.navItems ?? [];
  const agentTabs = web.agentTabs ?? [];
  const agentDetailSections = web.agentDetailSections ?? [];
  const agentHeaderBadges = web.agentHeaderBadges ?? [];
  const agentActions = web.agentActions ?? [];
  const homeWidgets = web.homeWidgets ?? [];
  const searchProviders = web.searchProviders ?? [];
  const attachments = web.attachments ?? [];
  const userSetupSteps = web.userSetupSteps ?? [];
  const settingsSections = web.settingsSections ?? [];
  const flowTriggerRenderers = web.flowTriggerRenderers ?? [];
  const pageTabs = web.pageTabs ?? [];
  const agentType = web.agentType;

  return [
    {
      key: 'pages',
      label: 'Pages',
      caption: 'New pages added to the sidebar.',
      items: navItems.map((n) => ({
        key: `nav-${n.to}`,
        label: n.label,
        icon: n.icon,
        href: n.to,
        rootLevel: n.rootLevel,
        openInNewWindow: n.openInNewWindow,
      })),
    },
    {
      key: 'agent-surfaces',
      label: 'On every agent',
      caption: 'Adds tabs, panels, badges, and actions to the agent detail view.',
      items: [
        ...agentTabs.map((t) => ({
          key: `tab-${t.id}`,
          label: t.label,
          hint: 'Tab',
          icon: t.icon,
        })),
        ...agentDetailSections.map((s) => ({
          key: `section-${s.id}`,
          label: s.label,
          hint: 'Sidebar section',
          icon: s.icon,
        })),
        ...agentHeaderBadges.map((b) => ({
          key: `badge-${b.id}`,
          label: b.id,
          hint: 'Header badge',
        })),
        ...agentActions.map((a, i) => ({
          key: `action-${i}-${a.label}`,
          label: a.label,
          hint: 'Action',
          icon: a.icon,
        })),
      ],
    },
    {
      key: 'home',
      label: 'On the home dashboard',
      caption: 'Widgets pinned to the home page strip.',
      items: homeWidgets.map((w) => ({
        key: `widget-${w.id}`,
        label: w.id,
        hint: 'Widget',
      })),
    },
    {
      key: 'palette',
      label: 'In the command palette',
      caption: 'Live search results in the global Cmd+K palette.',
      items: searchProviders.map((p) => ({
        key: `provider-${p.id}`,
        label: p.label,
        hint: p.id,
        icon: p.icon,
      })),
    },
    {
      key: 'agent-setup',
      label: 'Agent setup',
      caption: 'Choices that appear when starting an agent.',
      items: [
        ...attachments.map((a) => ({
          key: `attach-${a.id}`,
          label: a.label,
          hint: 'Attachment',
          icon: a.icon,
        })),
        ...userSetupSteps.map((s) => ({
          key: `setup-${s.id}`,
          label: s.label,
          hint: s.kind,
          icon: s.icon,
        })),
      ],
    },
    {
      key: 'settings',
      label: 'Settings',
      caption: 'Sections added to the global settings page.',
      items: settingsSections.map((s) => ({
        key: `settings-${s.id}`,
        label: s.label,
        hint: s.id,
        icon: s.icon,
      })),
    },
    {
      key: 'page-tabs',
      label: 'On other pages',
      caption: 'Tabs added to pages another plugin owns.',
      items: pageTabs.map((t) => ({
        key: `page-tab-${t.slot}-${t.id}`,
        label: t.label,
        hint: `${t.slot} tab`,
        icon: t.icon,
      })),
    },
    {
      key: 'flow-triggers',
      label: 'Automation triggers',
      caption: 'Renders custom automation trigger payloads.',
      items: flowTriggerRenderers.map((r) => ({
        key: `flow-${r.triggerType}`,
        label: r.label,
        hint: r.triggerType,
        icon: r.icon,
      })),
    },
    {
      key: 'agent-type',
      label: 'Provides this agent',
      caption: 'A new agent type you can launch from the new-agent screen.',
      items: agentType
        ? [
            {
              key: `agent-type-${agentType.name}`,
              label: agentType.name,
              hint: agentType.tagline,
              icon: agentType.icon,
            },
          ]
        : [],
    },
  ];
}

interface PluginContributionsSectionProps {
  manifest: PluginManifest | undefined;
}

/**
 * Lists every UI surface the plugin contributes to the web app, grouped
 * into user-meaningful categories ("Pages", "On every agent", etc.).
 *
 * The intent is to answer "what does this plugin actually add to my
 * workspace?" without forcing the user to install it first. Empty
 * categories are hidden; if the plugin contributes nothing the section
 * is omitted entirely.
 */
export function PluginContributionsSection({ manifest }: PluginContributionsSectionProps) {
  if (!manifest) return null;
  const categories = collect(manifest).filter((c) => c.items.length > 0);
  if (categories.length === 0) return null;

  return (
    <SectionCard
      data-testid="plugin-contributions-section"
      title="What this adds to your workspace"
      icon={<Sparkles />}
      contentClassName="flex flex-col gap-5"
    >
      {categories.map((category) => (
        <ContributionCategory key={category.key} category={category} />
      ))}
    </SectionCard>
  );
}

function ContributionCategory({ category }: { category: CategoryDef }): ReactNode {
  return (
    <div data-testid={`plugin-contributions-category-${category.key}`} className="space-y-2">
      <div className="flex items-baseline gap-2">
        <SectionLabel as="h3">{category.label}</SectionLabel>
        <span className="text-2xs font-medium tracking-wider text-muted-foreground/50">
          · {category.items.length}
        </span>
      </div>
      <p className="text-xs text-muted-foreground/70">{category.caption}</p>
      <ul className={cn('grid grid-cols-1 gap-2', category.key !== 'pages' && 'sm:grid-cols-2')}>
        {category.items.map((item) => (
          <ContributionItemRow key={item.key} categoryKey={category.key} item={item} />
        ))}
      </ul>
    </div>
  );
}

function resolveContributionHref(slug: string, item: ContributionItem): string | null {
  if (!item.href) return null;
  return item.rootLevel ? item.href : withOrgSlug(slug, item.href);
}

function ContributionItemRow({
  categoryKey,
  item,
}: {
  categoryKey: string;
  item: ContributionItem;
}): ReactNode {
  const slug = useActiveOrgSlug();
  const href = resolveContributionHref(slug, item);
  const isPageLink = categoryKey === 'pages' && href != null;
  const TrailingIcon = item.openInNewWindow ? ExternalLink : ChevronRight;

  const label = (
    <>
      {item.icon && (
        <LucideIconByName name={item.icon} className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      )}
      <span
        className={cn(
          'min-w-0 flex-1 text-sm font-medium text-foreground/90',
          !isPageLink && 'truncate',
        )}
      >
        {item.label}
      </span>
    </>
  );

  return (
    <MetaListItem
      variant="subtle"
      data-testid={`plugin-contribution-${categoryKey}-${item.key}`}
      className={cn(isPageLink && 'transition-colors hover:border-border/70 hover:bg-card')}
      trailing={
        href ? (
          <TrailingIcon aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
        ) : item.hint ? (
          <Badge variant="outline" className="text-3xs font-normal">
            {item.hint}
          </Badge>
        ) : undefined
      }
    >
      {href ? (
        item.openInNewWindow ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${item.label}`}
            className="flex min-w-0 flex-1 items-center gap-2 hover:underline"
          >
            {label}
          </a>
        ) : (
          <Link
            to={href}
            aria-label={`Open ${item.label}`}
            className="flex min-w-0 flex-1 items-center gap-2 hover:underline"
          >
            {label}
          </Link>
        )
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-2">{label}</div>
      )}
    </MetaListItem>
  );
}
